<template>
    <div class="field"
         :class="colSize">
        <label class="block"
               :class="labelClass"
               :style="labelStyle">{{ __(label) }}</label>
        <ToggleButton :disabled="isFormDisabled"
                      :name="fieldName"
                      :onLabel="onLabel"
                      :offLabel="offLabel"
                      :iconPos="iconPos"
                      :pt="toggleButtonPt"
                      :unstyled="true"
                      @change="handleChange"
                      v-focus="isFocus"
                      v-model="parseField">
            <template #icon="scope">
                <span v-if="scope.value"
                      :style="onIconStyle"
                      :class="[scope.class, onIconClass]"></span>
                <span v-if="!scope.value"
                      :style="offIconStyle"
                      :class="[scope.class, offIconClass]"></span>
            </template>
        </ToggleButton>
        <span :name="fieldName"></span>
    </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue';
import ToggleButton from 'primevue/togglebutton';
import { __ } from '@pages/Helper/utils.js'

const props = defineProps({
    fieldName: {
        type: String,
        required: true,
    },
    label: {
        type: String,
        default: null,
    },
    size: {
        type: String,
        default: null
    },
    isFocus: {
        type: Boolean,
        default: false
    },
    isFormDisabled: {
        type: Boolean,
        default: false
    },
    onLabel: {
        type: String,
        default: null
    },
    offLabel: {
        type: String,
        default: null
    },
    iconPos: {
        type: String,
        default: 'left'
    },
    onStyle: {
        type: [Object, String],
        default: null
    },
    offStyle: {
        type: [Object, String],
        default: null
    },
    onClass: {
        type: String,
        default: 'toggle-button-on'
    },
    offClass: {
        type: String,
        default: 'toggle-button-off'
    },
    onIconStyle: {
        type: [Object, String],
        default: "margin-right:7px;"
    },
    offIconStyle: {
        type: [Object, String],
        default: "margin-right:7px;"
    },
    onIconClass: {
        type: String,
        default: 'pi pi-check'
    },
    offIconClass: {
        type: String,
        default: 'pi pi-times'
    },
    labelClass: {
        type: String,
        default: null
    },
    labelStyle: {
        type: [Object, String],
        default: null
    },
    inRepeater: {
        type: Boolean,
        default: false
    },
    change: {
        type: Function,
        required: false
    },
    default: {
        type: Boolean,
        default: false
    }
});

const field = defineModel('field');
const colSize = props?.size ? 'col-' + props.size : 'col';

const onLabel = ref(props.onLabel ?? __('Yes'))
const offLabel = ref(props.offLabel ?? __('No'))

const emit = defineEmits(['change'])

const toggleButtonPt = {
    root: ({ context }) => ({
        class: [
            'w-full min-h-[2.5rem] inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm shadow-sm transition',
            'focus:outline-none focus:ring-2 focus:ring-black-500/50',
            context?.active
                ? 'bg-primary-50 text-blue-700 border-blue-200 hover:bg-primary-100'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
            context?.disabled ? 'opacity-60 cursor-not-allowed' : '',
            context?.active ? props.onClass : props.offClass
        ],
        style: context?.active ? props.onStyle : props.offStyle
    }),
    content: {
        class: 'inline-flex items-center justify-center gap-2 w-full'
    },
    label: {
        class: 'font-medium'
    }
}

// -------------------------------------------------------------------------------------------

const parseField = computed({
    get: () => {
        if (field.value === undefined) {
            field.value = props.default
        }
        return Boolean(field.value)
    },
    set: newValue => {
        field.value = newValue
        return Boolean(field.value)
    }
});

// -------------------------------------------------------------------------------------------
// Handler do przekazywania poprawnej wartości do callback
const handleChange = (event) => {
    nextTick(() => {
        // PrimeVue ToggleButton przekazuje event z właściwością value
        const newValue = event.value !== undefined ? event.value : parseField.value;
        // Wywołaj callback z poprawną wartością, jeśli został przekazany
        if (props.change && typeof props.change === 'function') {
            props.change(newValue);
        }
        emit('change', newValue)
    });
};

// -------------------------------------------------------------------------------------------

</script>

<style lang="scss" scoped></style>
